/* eslint-disable complexity */
import React, { FC, useState } from 'react';
import { Box, Button } from '@mui/material';
import { Showable, checkoutFromSubmitButtonId } from '@sweatpals/react-ui-kit';
import { StripeElements, StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { PaymentElement, AddressElement } from '@stripe/react-stripe-js';
import Image from 'next/legacy/image';
import { CheckoutFormDto } from '@/modules/_core/interfaces/checkout-form.dto';
import loader from '@/public/assets/images/loader.gif';
import { commonCheckoutFormStyles } from './checkout-form.styles';
import { useStripePaymentMethodType } from '@/modules/_core/hooks/use-stripe-payment-method-type.hook';


export interface PaymentElementsProps {
	elements: StripeElements;
	values: CheckoutFormDto;
	ticketsQuantity: number;
	isPolicyAgreed?: boolean;
	isPaying?: boolean;
	donationError?: string | null;
	isPriceAndFeesLoading?: boolean;
	commonStyles: ReturnType<typeof commonCheckoutFormStyles>;
	handlePay(elements: StripeElements, paymentMethodType: string, isVerificationRequired?: boolean): Promise<void>;
	setSelectedPaymentMethodType?(value: string | null): void;
}

export const PaymentElements: FC<PaymentElementsProps> = ({
	elements,
	values,
	ticketsQuantity,
	isPolicyAgreed,
	isPaying,
	donationError,
	isPriceAndFeesLoading,
	commonStyles,
	handlePay,
	setSelectedPaymentMethodType,
}) => {
	const paymentMethodType = useStripePaymentMethodType(setSelectedPaymentMethodType);
	const showAddressElement = paymentMethodType === 'klarna';

	const [addressValue, setAddressValue] = useState<null | StripeAddressElementChangeEvent['value']>(
		null
	);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '24px',
				opacity: isPaying ? 0.2 : 1,
			}}
		>
			<PaymentElement
				id="payment-element"
				options={{
					layout: 'tabs',
				}}
			/>
			<Showable show={showAddressElement}>
				{() => (
					<AddressElement
						id="address-element"
						options={{
							mode: 'billing',
							defaultValues: {
								name: values.fullName,
								address: {
									country: 'US',
								},
							},
						}}
						onChange={(event) => {
							if (event.complete) {
								setAddressValue(event.value);
							} else {
								setAddressValue(null);
							}
						}}
					/>
				)}
			</Showable>

			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<Button
					id={checkoutFromSubmitButtonId}
					sx={commonStyles.submitButton}
					disabled={
						!paymentMethodType ||
						!isPolicyAgreed ||
						!ticketsQuantity ||
						!!donationError ||
						isPriceAndFeesLoading ||
						(showAddressElement && !addressValue)
					}
					onClick={() => {
						if (!paymentMethodType) return;

						handlePay(elements, paymentMethodType);
					}}
				>
					Pay with card
				</Button>
			</Box>
		</Box>
	);
};
